import { ChangeEvent, useCallback, useLayoutEffect, useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { TwinAutoSizer } from '../../../baseComponents/TwinTable/Adapters'
import useMyDebounce, { MyDebounceProps } from '../../../utils/hooks/useMyDebounce'
import './textarea.sass'

export type TextAreaProps = TextAreaLogicProps & {
    value?: string
    className?: string
    name?: string
    label?: string
    placeholder?: string
    onBlur?: React.FocusEventHandler<HTMLTextAreaElement>
    readOnly?: boolean
    subRef?: any
    maxRows?: number
    required?: boolean
    parentClassName?: string
    hideLabel?: boolean
}

const TextArea: React.FC<TextAreaProps> = ({ label, value, className, onChange, placeholder, onBlur, subRef, maxRows = 6, parentClassName, hideLabel, ...rest }) => {
    const { myOnChange, changeWidth, isLoaded, setFocusedTrue, myOnBlur } = useTextAreaLogic({ onChange, onBlur })
    return (
        <div className={'flex flex-col w-fill ' + (parentClassName || '') }>
            <TwinAutoSizer disableHeight={true} onResize={changeWidth} >
                {({ width }) => {
                    return (
                        <div className={'flex flex-auto flex-col justify-end items-start custom_textarea_parent ' + (className || '')} style={{ width }}>
                            {!hideLabel ? <span className='text_area_label'>{label} {label && rest.required ? '*' : ''}</span> : null}
                            {isLoaded === 1 && width > 10 ? <TextareaAutosize ref={subRef} onBlur={myOnBlur} onFocus={setFocusedTrue} onChange={myOnChange} placeholder={placeholder} maxRows={maxRows} className={'input_white_border_class w-full'} value={value} {...rest} /> : null}
                        </div>
                    )
                }}
            </TwinAutoSizer>
        </div>

    )
}

export const TextAreaWhiteInput: React.FC<TextAreaProps> = ({ label, value, className, onChange, placeholder, onBlur, subRef, maxRows = 6, parentClassName, ...rest }) => {
    const { myOnChange, changeWidth, isLoaded, setFocusedTrue, myOnBlur } = useTextAreaLogic({ onChange, onBlur })
    return (
        <div className={'flex flex-col w-fill ' + (parentClassName || '') }>
            <TwinAutoSizer disableHeight={true} onResize={changeWidth} >
                {({ width }) => {
                    return (
                        <div className={'flex flex-auto flex-col justify-end items-start custom_textarea_parent ' + (className || '')} style={{ width }}>
                            <span className='text_area_label'>{label} {label && rest.required ? '*' : ''}</span>
                            {isLoaded === 1 && width > 10 ? <TextareaAutosize ref={subRef} onBlur={myOnBlur} onFocus={setFocusedTrue} onChange={myOnChange} placeholder={placeholder} maxRows={maxRows} className={'input_white_border_class w-full'} value={value} {...rest} /> : null}
                        </div>
                    )
                }}
            </TwinAutoSizer>
        </div>

    )
}

export interface RectangularTextAreaProps extends TextAreaProps {
    subRef?: any
}

export const RectangularTextArea: React.FC<RectangularTextAreaProps> = ({ label, value, onChange, placeholder, onBlur, className, subRef, onShiftEnter, maxRows = 6, ...rest }) => {
    const { myOnChange, isLoaded, changeWidth, myOnBlur, setFocusedTrue, myOnShiftEnter } = useTextAreaLogic({ onChange, onBlur, onShiftEnter })
    return (
        <div className='flex flex-col w-fill'>
            <TwinAutoSizer disableHeight={true} onResize={changeWidth}>
            {({ width }) => {
                return (
                    <div className={'rectangular_textarea_parent flex flex-col justify-end items-start border border-gray-D6 ' + (className || '')} style={{ width }}>
                        <span>{label}</span>
                        <div className='w-full rectangular_textarea flex justify-center flex-col'>
                            {isLoaded === 1 && width > 10 ? <TextareaAutosize ref={subRef} onBlur={myOnBlur} onKeyDownCapture={myOnShiftEnter} onFocus={setFocusedTrue} onChange={myOnChange} placeholder={placeholder} maxRows={maxRows} className={'resize-none border-0 w-full h-0 p-0'} value={value} {...rest} /> : null}
                        </div>
                    </div>

                )
                }}
            </TwinAutoSizer>
        </div>
    )
}

interface TextAreaLogicProps {
    onShiftEnter?: () => void
    onChange?: (value: string) => void
    onBlur?: React.FocusEventHandler<HTMLTextAreaElement> | undefined
}

const useTextAreaLogic = ({ onChange, onBlur, onShiftEnter }: TextAreaLogicProps) => {
    const [isLoaded, setIsLoaded] = useState(0)
    const [isFocused, setIsFocused] = useState(false)

    const myOnShiftEnter: React.KeyboardEventHandler<HTMLTextAreaElement> = useCallback((e) => {
        if (e.key === 'Enter' && e.shiftKey) {
            e.preventDefault()
            e.stopPropagation()
            onShiftEnter?.()
        }
    }, [onShiftEnter])

    const myOnChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
        onChange?.(e.currentTarget.value)
    }, [onChange])

    const myOnBlur: React.FocusEventHandler<HTMLTextAreaElement> = useCallback((e) => {
        setIsFocused(false)
        onBlur?.(e)
    }, [onBlur])

    const setFocusedTrue = useCallback(() => {
        setIsFocused(true)
    }, [setIsFocused])

    const changeWidth = useCallback(() => {
        if (!isFocused){
            setIsLoaded(0)
        }
    }, [isFocused])

    useLayoutEffect(() => {
        if (isLoaded === 0) {
            setIsLoaded(1)
        }
    }, [setIsLoaded, isLoaded])

    return { myOnChange, changeWidth, isLoaded, myOnBlur, setFocusedTrue, myOnShiftEnter }
}

type TextAreaDebounceProps = TextAreaProps & MyDebounceProps & {}

export const TextAreaDebounce: React.FC<TextAreaDebounceProps> = ({ value, onChange,  ...rest }) => {
    const { changeMyValue, myValue, ref, myOnBlur } = useMyDebounce({ value, onChange, readOnly: rest.readOnly })
    return (
        <TextArea {...rest} value={myValue} onChange={changeMyValue} subRef={ref} onBlur={myOnBlur}/>
    )
}

type RectangularTextAreaDebounceProps = RectangularTextAreaProps & MyDebounceProps & {}

export const RectangularTextAreaDebounce: React.FC<RectangularTextAreaDebounceProps> = ({ value, onChange, onBlur, ...rest }) => {
    const { changeMyValue, myValue, ref, myOnBlur } = useMyDebounce({ value, onChange, readOnly: rest.readOnly })
    return (
        <RectangularTextArea {...rest} value={myValue} onChange={changeMyValue} subRef={ref} onBlur={myOnBlur} />
    )
}

export interface TextAreaStateFullProps extends TextAreaProps { }

export const TextAreaStateFull: React.FC<TextAreaStateFullProps> = ({ value, ...props }) => {
    const { myOnChange, myValue } = useTextAreaStateFullLogic({ value })
    return (
        <TextArea {...props} value={myValue} onChange={myOnChange} />
    )
}

interface TextAreaStateFullLogicProps {
    value?: string
    onChange?: (value: string) => void
}

const useTextAreaStateFullLogic = ({ value, onChange }: TextAreaStateFullLogicProps) => {
    const [myValue, setMyValue] = useState(value || '')

    const myOnChange = useCallback((value: string) => {
        setMyValue(value)
        onChange?.(value)
    }, [onChange])

    return { myValue, myOnChange }
}


export default TextArea